/**
 * Frontend Xero tracking service.
 * Calls xero-setup-tracking edge function and queries tracking status.
 */

import { supabase } from '../../lib/supabase'
import { getCurrentUserId } from '../../lib/auth'

// ── Types ──────────────────────────────────────────────────────

interface TrackingSetupResult {
  success: boolean
  partial?: boolean
  properties_synced?: number
  properties_failed?: number
  portfolios_synced?: number
  portfolios_failed?: number
  error?: string
}

interface TrackingStatus {
  property_category_set: boolean
  client_category_set: boolean
  properties_with_tracking: number
  properties_without_tracking: number
}

// ── Exported functions ─────────────────────────────────────────

export async function setupXeroTracking(): Promise<TrackingSetupResult> {
  const { data, error } = await supabase.functions.invoke('xero-setup-tracking')

  if (error) {
    return { success: false, error: 'Failed to set up tracking categories. You can retry from Settings.' }
  }

  if (data?.partial) {
    return {
      success: true,
      partial: true,
      properties_synced: data.result?.propertiesSynced ?? 0,
      properties_failed: data.result?.propertiesFailed ?? 0,
      portfolios_synced: data.result?.portfoliosSynced ?? 0,
      portfolios_failed: data.result?.portfoliosFailed ?? 0,
      error: data.error,
    }
  }

  return {
    success: data?.success ?? false,
    properties_synced: data?.result?.propertiesSynced ?? 0,
    properties_failed: data?.result?.propertiesFailed ?? 0,
    portfolios_synced: data?.result?.portfoliosSynced ?? 0,
    portfolios_failed: data?.result?.portfoliosFailed ?? 0,
  }
}

export async function getTrackingStatus(): Promise<TrackingStatus> {
  const userId = await getCurrentUserId()

  // Check connection for category IDs
  const { data: conn } = await supabase
    .from('xero_connections')
    .select('xero_property_tracking_category_id, xero_client_tracking_category_id')
    .eq('owner_id', userId)
    .eq('is_active', true)
    .maybeSingle()

  // Count properties with/without tracking
  const { count: withTracking } = await supabase
    .from('properties')
    .select('*', { count: 'exact', head: true })
    .eq('owner_id', userId)
    .is('deleted_at', null)
    .not('xero_tracking_option_id', 'is', null)

  const { count: withoutTracking } = await supabase
    .from('properties')
    .select('*', { count: 'exact', head: true })
    .eq('owner_id', userId)
    .is('deleted_at', null)
    .is('xero_tracking_option_id', null)

  return {
    property_category_set: !!conn?.xero_property_tracking_category_id,
    client_category_set: !!conn?.xero_client_tracking_category_id,
    properties_with_tracking: withTracking ?? 0,
    properties_without_tracking: withoutTracking ?? 0,
  }
}
